import { api } from "@/lib/apiClient";

// ─── Umsatz & Zahlungen ──────────────────────────────────────────────────────

export interface InvoiceRevenueMonth {
  month: string;
  invoice_count: number;
  total_amount: number;
  paid_amount: number;
  open_amount: number;
  /** Nur in der kombinierten Monatsreihe. */
  hero_amount?: number;
  app_amount?: number;
  /** Entwürfe (Hero + App), nicht im Umsatz enthalten. */
  draft_amount?: number;
}

export interface InvoiceRevenueCustomer {
  kunde: string | null;
  kunden_id: number | null;
  invoice_count: number;
  total_amount: number;
  paid_amount: number;
  open_amount: number;
}

export interface InvoiceRevenueAuftragsart {
  auftragsart: string | null;
  invoice_count: number;
  total_amount: number;
}

export interface InvoiceRevenueAmountSummary {
  invoice_count: number;
  total_amount: number;
  paid_amount: number;
  open_amount: number;
}

export interface InvoiceRevenueForecast {
  as_of: string;
  year_complete: boolean;
  elapsed_days: number;
  days_in_year: number;
  elapsed_ratio: number;
  average_per_day: number;
  average_per_month: number;
  projected_invoice_amount: number;
  projected_valid_amount: number;
  confidence: "unsicher" | "tendenz" | "belastbar" | "valide" | "ist";
}

export interface InvoiceRevenueReport {
  year: number;
  available_years: number[];
  last_synced_at: string | null;
  /** Manuell erfasste Vorkasse aus dem Vorjahr. */
  prior_year_vorkasse?: number;
  /** Rechnungsumsatz plus Vorkasse Vorjahr. */
  valid_revenue?: number;
  forecast?: InvoiceRevenueForecast;
  /** Gesamtumsatz Hero + App-eigene Rechnungen (netto, additiv). */
  summary: InvoiceRevenueAmountSummary & {
    avg_payment_days: number | null;
    median_payment_days: number | null;
    paid_with_date_count: number;
  };
  by_month: InvoiceRevenueMonth[];
  hero_summary?: InvoiceRevenueAmountSummary;
  hero_by_month?: InvoiceRevenueMonth[];
  /** App-eigene Rechnungen ohne Hero-Verknüpfung (netto, Belegdatum). */
  app_summary?: InvoiceRevenueAmountSummary;
  app_by_month?: InvoiceRevenueMonth[];
  top_customers: InvoiceRevenueCustomer[];
  /** Hero- und App-Rechnungen, gruppiert nach Auftragsart. */
  by_auftragsart: InvoiceRevenueAuftragsart[];
}

export async function fetchInvoiceRevenueReport(year: number): Promise<InvoiceRevenueReport> {
  return api.get<InvoiceRevenueReport>(`/reports/invoice-revenue?year=${year}`);
}

export async function savePriorYearVorkasse(
  year: number,
  amount: number,
): Promise<{ year: number; prior_year_vorkasse: number }> {
  return api.put<{ year: number; prior_year_vorkasse: number }>(
    `/reports/invoice-revenue/prior-vorkasse`,
    { year, amount },
  );
}

// ─── Abstimmung Hero-Rechnungsvolumen ────────────────────────────────────────

export interface InvoiceRevenueReconcileBucket {
  key: string;
  invoice_count: number;
  total_amount: number;
}

export interface InvoiceRevenueReconcileVariant {
  key: string;
  label: string;
  invoice_count: number;
  total_amount: number;
  paid_amount: number;
  open_amount: number;
  diff_to_target: number;
  abs_diff_to_target: number;
}

export type InvoiceRevenueExcludeReason =
  | "status_storniert"
  | "stornorechnung_oder_korrektur"
  | "ohne_zahlstatus_bezahlt_offen"
  | "andere";

export const INVOICE_EXCLUDE_REASON_LABELS: Record<InvoiceRevenueExcludeReason, string> = {
  status_storniert: "Status Storniert",
  stornorechnung_oder_korrektur: "Stornorechnung / Korrektur",
  ohne_zahlstatus_bezahlt_offen: "Ohne Zahlstatus Bezahlt/Offen",
  andere: "Andere",
};

export interface InvoiceRevenueReconcileDoc {
  hero_document_id: number;
  document_number: string | null;
  document_type: string | null;
  document_status_name: string | null;
  booking_status_name: string | null;
  belegdatum: string | null;
  total_amount: number;
  exclude_reason: InvoiceRevenueExcludeReason | string;
}

export interface InvoiceRevenueReconcileReport {
  year: number;
  hero_target: number;
  last_synced_at: string | null;
  current: InvoiceRevenueAmountSummary & { diff_to_target: number };
  by_status: InvoiceRevenueReconcileBucket[];
  by_type: InvoiceRevenueReconcileBucket[];
  variants: InvoiceRevenueReconcileVariant[];
  best_variant_key: string;
  excluded_documents: InvoiceRevenueReconcileDoc[];
  excluded_documents_count: number;
  excluded_documents_sum: number;
}

export async function fetchInvoiceRevenueReconcile(
  year: number,
  heroTarget = 816303.29,
): Promise<InvoiceRevenueReconcileReport> {
  const params = new URLSearchParams({
    year: String(year),
    hero_target: String(heroTarget),
  });
  return api.get<InvoiceRevenueReconcileReport>(
    `/reports/invoice-revenue/reconcile?${params}`,
  );
}

// ─── Offene Posten ───────────────────────────────────────────────────────────

export type OpenInvoiceBucket = "not_due" | "d1_30" | "d31_60" | "d61_plus" | "no_due_date";

export const OPEN_INVOICE_BUCKETS: OpenInvoiceBucket[] = [
  "not_due",
  "d1_30",
  "d31_60",
  "d61_plus",
  "no_due_date",
];

export const OPEN_INVOICE_BUCKET_LABELS: Record<OpenInvoiceBucket, string> = {
  not_due: "Noch nicht fällig",
  d1_30: "1–30 Tage überfällig",
  d31_60: "31–60 Tage überfällig",
  d61_plus: "Über 60 Tage überfällig",
  no_due_date: "Ohne Fälligkeitsdatum",
};

export interface OpenInvoiceItem {
  /** Eindeutig über beide Quellen, z. B. "gd:123" oder "hero:456". */
  id: string;
  quelle: "app" | "hero";
  dokument_id: number | null;
  hero_document_id: number | null;
  document_number: string | null;
  document_name: string | null;
  status_name: string | null;
  booking_status_name: string | null;
  kunde: string;
  kunden_id: number | null;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  beschreibung: string | null;
  invoice_date: string | null;
  due_date: string | null;
  days_overdue: number | null;
  bucket: OpenInvoiceBucket;
  total_amount: number;
  open_amount: number;
}

export interface OpenInvoiceCustomer {
  kunde: string;
  kunden_id: number | null;
  open_count: number;
  open_amount: number;
  max_days_overdue: number;
}

export interface OpenInvoicesReport {
  as_of: string;
  last_synced_at: string | null;
  summary: {
    open_count: number;
    open_amount: number;
    overdue_count: number;
    overdue_amount: number;
    buckets: Record<OpenInvoiceBucket, { count: number; amount: number }>;
  };
  items: OpenInvoiceItem[];
  by_customer: OpenInvoiceCustomer[];
}

export async function fetchOpenInvoicesReport(): Promise<OpenInvoicesReport> {
  return api.get<OpenInvoicesReport>(`/reports/open-invoices`);
}

// ─── Angebots-Statistik ──────────────────────────────────────────────────────

export interface OfferStatisticsMonth {
  month: string;
  offer_count: number;
  offer_amount: number;
  won_count: number;
  won_amount: number;
  draft_count: number;
}

export interface OfferStatusRow {
  status: string;
  count: number;
  amount: number;
}

export interface OpenOfferItem {
  hero_document_id: number;
  document_number: string | null;
  document_name: string | null;
  status_name: string | null;
  kunde: string | null;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  beschreibung: string | null;
  amount: number;
  created_at: string | null;
}

export interface OfferStatisticsReport {
  year: number;
  available_years: number[];
  last_synced_at: string | null;
  summary: {
    offer_count: number;
    offer_amount: number;
    won_count: number;
    won_amount: number;
    draft_count: number;
    open_count: number;
    conversion_rate: number | null;
    conversion_rate_amount: number | null;
  };
  by_month: OfferStatisticsMonth[];
  by_status: OfferStatusRow[];
  open_offers: OpenOfferItem[];
}

export async function fetchOfferStatisticsReport(year: number): Promise<OfferStatisticsReport> {
  return api.get<OfferStatisticsReport>(`/reports/offer-statistics?year=${year}`);
}

// ─── Marge & Lohn/Material ───────────────────────────────────────────────────

export interface DocumentMarginMonth {
  month: string;
  netto: number;
  lohn: number;
  material: number;
}

export interface DocumentMarginItem {
  hero_document_id: number;
  document_number: string | null;
  betreff: string | null;
  kunde: string | null;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  beschreibung: string | null;
  doc_date: string | null;
  netto: number;
  basis_kosten: number | null;
  lohn: number;
  material: number;
  zeit_minuten: number | null;
  marge_amount: number | null;
  marge_percent: number | null;
}

export interface DocumentMarginsReport {
  year: number;
  typ: "rechnung" | "angebot";
  available_years: number[];
  last_synced_at: string | null;
  coverage: {
    documents_total: number;
    documents_with_details: number;
    coverage_rate: number | null;
  };
  summary: {
    netto_sum: number;
    lohn_sum: number;
    material_sum: number;
    basis_kosten_sum: number | null;
    marge_amount: number | null;
    marge_percent: number | null;
  };
  by_month: DocumentMarginMonth[];
  items: DocumentMarginItem[];
}

export async function fetchDocumentMarginsReport(
  year: number,
  typ: "rechnung" | "angebot" = "rechnung",
): Promise<DocumentMarginsReport> {
  return api.get<DocumentMarginsReport>(`/reports/document-margins?year=${year}&typ=${typ}`);
}

// ─── Formatierung ────────────────────────────────────────────────────────────

export function formatEuroDe(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "–";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount);
}

export function formatEuroCompact(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "–";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPercentDe(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return "–";
  return `${(ratio * 100).toFixed(1).replace(".", ",")} %`;
}

export function formatMonthShortDe(month: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString("de-DE", { month: "short" });
}

export function formatDateTimeDe(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateShortDe(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}
